import { useTheme } from '@mui/material';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import {
  athleteDisplayName,
  CRICKET_BOWLING_COACH_PROFILE_ROLE,
} from '@strength/domain';
import { I18nTranslator } from '@strength/i18n';
import { AppSessionData } from '@strength/server-auth';
import { useMatches, useNavigate, useParams } from '@tanstack/react-router';
import {
  IconType,
  MdBuild,
  MdCollectionsBookmark,
  MdDynamicForm,
  MdForum,
  MdHome,
  MdHub,
  MdList,
  MdMenuBook,
  MdPeople,
  MdSettings,
  MdSpaceDashboard,
  MdTablet,
  MdTask,
  MdUpload,
} from '@teamworksdev/icons';
import { PrimaryNav, PrimaryNavLink, Team } from '@teamworksdev/nav';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, useLogout, useSessionSwitch } from '../auth';
import { environment } from '../environment';
import { RouterOutput } from '../trpc';
import { trpc } from '../trpc/react';
import { StrengthIcon } from './components/icons/strength-icon';
import { useFlags } from './context/featureFlag';
import { useViewer } from './context/viewer';
import { useLibraryTabs } from './pages/library/library-tabs';
import { canAccessPermissions } from './permissions-access';
type PrimaryNavProps = React.ComponentProps<typeof PrimaryNav>;

const LockPersonNavIcon: IconType = (props) => (
  <LockPersonIcon
    className={props.className}
    aria-hidden={props['aria-hidden']}
  />
);

export function Navbar({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const logout = useLogout();
  const sessionSwitch = useSessionSwitch();
  const { data } = trpc.user.profilesForSwitch.useQuery();
  const getSession = trpc.user.getSessionToken.useMutation();
  const { mavenSupportChatFlag } = useFlags();
  const { data: mavenSignedUserData } = trpc.user.mavenSignedUserData.useQuery(
    undefined,
    {
      enabled:
        mavenSupportChatFlag &&
        auth?.type === 'org' &&
        !environment.VITE_IS_GOV,
    },
  );

  const {
    navbar: {
      navbarBottomColor,
      navbarTopColor,
      appTargetUrls,
      canManageAppAccess,
    },
  } = useViewer();

  const hubAppTargetUrl = appTargetUrls?.['hub'] ?? '';
  const onUserManagementClick = async () => {
    const token = await getSession.mutateAsync({
      profileId: auth?.type === 'org' ? auth.profileId : '',
    });
    window.location.href = `${hubAppTargetUrl}?redirect_with_token=true&token=${token}&redirect=/profiles`;
  };
  const { primaryLinks, bottomLinks } = useLinksForNavbar(
    auth,
    onUserManagementClick,
    canManageAppAccess,
  );

  const {
    palette: {
      products: { performance },
      common: { black },
    },
  } = useTheme();

  const { teams, team } = teamsForNavbar(auth, data, t);

  const onAppChange = async (app: string, url: string) => {
    const token = await getSession.mutateAsync({
      profileId: auth?.type === 'org' ? auth.profileId : '',
    });
    window.location.href = `${url}?token=${token}`;
  };

  const helpLinks: Partial<PrimaryNavProps> = environment.VITE_IS_GOV
    ? {
        hasLearningPortal: false,
        hasHelpCenter: false,
        appSpecificHelpSubmenu: {
          hideDivider: true,
          links: [
            {
              name: t('getHelp'),
              onClick: () => {
                window.open('mailto:tacsupport@teamworks.com', '_blank');
              },
            },
            {
              name: t('helpCenter'),
              to: 'https://help.teamworkstactical.com/strength/s/',
              target: '_blank',
            },
          ],
        },
      }
    : {
        hasMavenSupportChat: mavenSupportChatFlag,
        mavenTags: ['strengthconditioning'],
        mavenSignedUserData,
      };

  return (
    <div
      style={{ borderColor: navbarTopColor, backgroundColor: navbarTopColor }}
      // Override PrimaryNav's h-dvh (100dvh) so it respects the container height when GOV banners reduce available space
      className="flex w-screen h-full border-black border-t-8 overflow-hidden [&_#tw-shared-nav]:!h-full"
    >
      <PrimaryNav
        app="strength"
        apps={appTargetUrls}
        hasAppSwitcher={true}
        minAppsForSwitcher={1}
        colors={{
          bottom: navbarBottomColor ?? performance.primary,
          top: navbarTopColor ?? black,
        }}
        links={primaryLinks}
        bottomLinks={bottomLinks}
        logo={
          <StrengthIcon sx={{ alignSelf: 'center', width: 36, height: 36 }} />
        }
        team={team}
        teams={teams}
        user={
          data?.currentProfile
            ? {
                name: athleteDisplayName(data.currentProfile),
              }
            : auth?.type === 'admin'
              ? { name: t('admin') }
              : { name: '' }
        }
        onTeamChange={(id: string) => {
          // sessionSwitch full-reloads to the new org; no client-side nav
          sessionSwitch(
            id === ADMIN_ID ? { admin: true } : { admin: false, profileId: id },
          );
        }}
        onAppChange={onAppChange}
        onLogout={async () => {
          await logout();
        }}
        {...helpLinks}
      />
      <div className="bg-white rounded-tl-2xl overflow-hidden flex grow flex-col min-w-0">
        {children}
      </div>
    </div>
  );
}

function useLinksForNavbar(
  auth: AppSessionData | null,
  onUserManagementClick: () => void,
  canManageAppAccess: boolean,
): {
  primaryLinks: PrimaryNavLink[];
  bottomLinks: PrimaryNavLink[];
} {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const matches = useMatches();
  // Permission-filtered library tabs — used to send the Library nav link to
  // the first tab the viewer can actually see.
  const libraryTabs = useLibraryTabs();
  const firstLibraryTab = libraryTabs[0];
  const { unityMessaging, developerToolsFlag, aiFlag, workoutImporterFlag } =
    useFlags();

  const buildNavLink = useCallback(
    (
      to: string,
      name: string,
      icon: IconType,
      opts?: { isActivePath?: string; onClick?: () => void },
    ) => ({
      to,
      name,
      icon: <NavBarIcon IconPath={icon} />,
      isActive: matches.some((m) =>
        m.pathname.includes(opts?.isActivePath ?? to),
      ),
      onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();
        if (opts?.onClick) {
          opts.onClick();
        } else {
          navigate({
            to,
            params: {},
          });
        }
      },
    }),
    [navigate, matches],
  );

  const {
    team: { selectedTeam },
    profile: { hasAmsDashboardAccess },
    hasPermissions,
  } = useViewer();

  const param = useParams({ strict: false });

  if (!auth) {
    return { primaryLinks: [], bottomLinks: [] };
  } else if (auth.type === 'org') {
    const canViewActuals = hasPermissions('actuals', 'view');
    const canViewNotes = hasPermissions('notes', 'view');
    const canViewTablet = hasPermissions('tablet', 'view');
    // Teams disappears only when all four Teams-area permissions are off;
    // any single grant keeps the nav item (SC-4379).
    const canViewTeams =
      hasPermissions('teams.profiles', 'view') ||
      hasPermissions('teams.activityLog', 'view') ||
      hasPermissions('teams.benchmarks', 'view') ||
      hasPermissions('teams.settings', 'view');

    const tabletRoutes: PrimaryNavLink[] = canViewTablet
      ? [
          buildNavLink('/actuals/tablet', t('tabletMode'), MdTablet, {
            onClick: () => {
              navigate({
                to: '/actuals/tablet',
              });
            },
          }),
        ]
      : [];

    const bottomLinks: PrimaryNavLink[] = canManageAppAccess
      ? [
          buildNavLink('/userManagement', t('userManagement'), MdMenuBook, {
            onClick: onUserManagementClick,
          }),
        ]
      : [];

    const planLink: PrimaryNavLink = buildNavLink('/plan', t('plan'), MdHome, {
      onClick: () => {
        if (selectedTeam) {
          navigate({
            to: `/$teamId/plan`,
            params: { teamId: selectedTeam },
          });
        } else {
          navigate({
            to: '/plan',
          });
        }
      },
    });
    const notesLink: PrimaryNavLink = buildNavLink(
      '/notes',
      t('notes'),
      MdTask,
      {
        isActivePath: '/notes',
        onClick: () => {
          if (selectedTeam) {
            navigate({
              to: `/$teamId/notes`,
              params: { teamId: selectedTeam },
            });
          } else {
            navigate({ to: '/notes' });
          }
        },
      },
    );

    if (auth.role === CRICKET_BOWLING_COACH_PROFILE_ROLE) {
      return {
        primaryLinks: [
          // only allow access to plan and notes
          planLink,
          notesLink,
        ],
        bottomLinks,
      };
    }

    return {
      primaryLinks: [
        planLink,
        ...(unityMessaging
          ? [
              buildNavLink('/messaging', t('messaging'), MdForum, {
                isActivePath: '/messaging',
              }),
            ]
          : []),
        ...(hasAmsDashboardAccess
          ? [buildNavLink('/reporting', t('reporting'), MdSpaceDashboard)]
          : []),
        ...(canViewActuals
          ? [
              buildNavLink('/actuals', t('actuals'), MdDynamicForm, {
                onClick: () => {
                  if (selectedTeam) {
                    const date = 'date' in param ? param.date : '';
                    navigate({
                      to: `/$teamId/actuals/$date`,
                      params: { teamId: selectedTeam, date },
                    });
                  } else {
                    navigate({
                      to: '/actuals',
                    });
                  }
                },
              }),
            ]
          : []),
        ...(canViewNotes ? [notesLink] : []),
        ...(canViewTeams
          ? [
              buildNavLink('/team', t('teams'), MdPeople, {
                onClick: () => {
                  if (selectedTeam) {
                    navigate({
                      to: `/$teamId/team/profiles`,
                      params: { teamId: selectedTeam },
                    });
                  } else {
                    navigate({
                      to: '/team',
                    });
                  }
                },
              }),
            ]
          : []),
        ...(canAccessPermissions(auth.role, true)
          ? [buildNavLink('/permissions', t('permissions'), LockPersonNavIcon)]
          : []),
        // Hide the Library link entirely when the viewer has no access to
        // any library tab.
        ...(firstLibraryTab
          ? [
              buildNavLink('/library', t('library'), MdCollectionsBookmark, {
                isActivePath: '/library',
                onClick: () => {
                  if (selectedTeam) {
                    navigate({
                      to: firstLibraryTab.to,
                      params: { teamId: selectedTeam },
                    });
                  } else {
                    navigate({ to: '/library' });
                  }
                },
              }),
            ]
          : []),
        ...tabletRoutes,
      ],
      bottomLinks,
    };
  } else {
    return {
      primaryLinks: [
        {
          ...buildNavLink('/admin/orgs', 'Orgs', MdHome),
        },
        {
          ...buildNavLink(
            '/admin/exercise-library-management',
            'Exercise Library',
            MdList,
          ),
        },
        {
          ...buildNavLink('/admin/metrics', 'Metrics', MdSettings),
        },
        ...(workoutImporterFlag
          ? [
              {
                ...buildNavLink(
                  '/admin/workout-import',
                  'Workout Import',
                  MdUpload,
                ),
              },
            ]
          : []),
        {
          ...buildNavLink('/admin/tum-sync', 'TUM Sync', MdPeople),
        },
        ...(aiFlag
          ? [
              {
                ...buildNavLink('/admin/ai', 'AI', MdHub),
              },
            ]
          : []),
        ...(developerToolsFlag
          ? [buildNavLink('/admin/developers', 'Developers', MdBuild)]
          : []),
      ],
      bottomLinks: [],
    };
  }
}
const NavBarIcon = ({ IconPath }: { IconPath: IconType }) => {
  return <IconPath className="text-white h-5 w-5" aria-hidden="true" />;
};

type ProfilesForSwitch = RouterOutput['user']['profilesForSwitch'];

const ADMIN_ID = 'admin';

function teamsForNavbar(
  auth: AppSessionData | null,
  data: ProfilesForSwitch | undefined,
  t: I18nTranslator,
): {
  teams: Team[] | undefined;
  team: Team;
} {
  if (!auth || !data || !showSwitcher(data)) {
    return {
      teams: undefined,
      team: { id: '', name: '' },
    };
  }

  const teams: Team[] = [];
  if (data.isAdmin) {
    teams.push({ id: ADMIN_ID, name: t('admin') });
  }
  for (const profile of data.profiles) {
    teams.push({
      id: profile.id,
      avatar: profile.navbarImageUrl ?? undefined,
      name: profile.orgName,
    });
  }

  const team = teams.find((team) => {
    if (auth.type === 'org') {
      return team.id === auth.profileId;
    } else {
      return team.id === ADMIN_ID;
    }
  }) ?? { id: '', name: '' };

  return { teams, team };
}

function showSwitcher(data: ProfilesForSwitch): boolean {
  const profileCount = data.profiles.length + (data.isAdmin ? 1 : 0);
  return profileCount >= 1;
}
